import api from './api';

export const authService = {
  login: async (credentials) => {
    try {
      
      const response = await api.post('/api/business/auth/login', credentials);

      if (response.data?.success && response.data?.data) {
        const { business, restaurantId } = response.data.data;
        
        // Business objesini kontrol et
        if (!business) {
          throw new Error('İşletme bilgileri alınamadı');
        }

        // Token artık httpOnly cookie'de, localStorage'a kaydetme
        // Sadece business bilgilerini localStorage'a kaydet (token değil)
        localStorage.setItem('business', JSON.stringify(business));
        localStorage.setItem('restaurantId', restaurantId);
        localStorage.setItem('userType', 'business');
        
        return {
          success: true,
          data: { business, restaurantId }
        };
      }
      
      throw new Error(response.data?.message || 'Giriş başarısız');
      
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        originalError: error
      });
      
      // Backend'den gelen hata mesajını kullan
      if (error.response?.data?.message) {
        let errorMessage = error.response.data.message;
        
        // Status'a göre daha detaylı mesajlar
        if (error.response.data.status === 'pending') {
          errorMessage = 'İşletmeniz henüz onay sürecindedir. Onaylandıktan sonra giriş yapabilirsiniz.';
        } else if (error.response.data.status === 'rejected') {
          errorMessage = 'İşletmeniz reddedilmiştir. Detaylı bilgi için yönetici ile iletişime geçiniz.';
        } else if (error.response.data.status === 'passive') {
          errorMessage = 'İşletmeniz pasif durumdadır. Aktifleştirmek için yönetici ile iletişime geçiniz.';
        }

        const authError = new Error(errorMessage);
        authError.status = error.response.data.status || error.response?.status;
        throw authError;
      }
      
      // Genel hata durumu
      const authError = new Error(error.message || 'Giriş yapılırken bir hata oluştu');
      authError.status = error.response?.status;
      throw authError;
    }
  },

  register: async (formData) => {
    try {
      // Address verisini al ve parse et
      const addressData = JSON.parse(formData.get('address'));
      const workingHoursData = JSON.parse(formData.get('workingHours'));

      // Restaurant verilerini düzgün formatta hazırla
      const restaurantData = {
        name: formData.get('businessName'),
        email: formData.get('email'),
        phone: formData.get('businessPhone'),
        description: formData.get('description'),
        address: {
          street: addressData.street,
          city: addressData.city,
          district: addressData.district,
          fullAddress: addressData.fullAddress,
          location: {
            type: 'Point',
            coordinates: [28.9784, 41.0082]
          }
        },
        businessType: formData.get('businessType'),
        socialMedia: {
          instagram: '',
          facebook: '',
          twitter: '',
          linkedin: ''
        },
        website: ''
      };

      // FormData'yı yeniden oluştur
      const newFormData = new FormData();

      // Business bilgilerini ekle
      for (let [key, value] of formData.entries()) {
        if (key === 'address') {
          newFormData.append('address', JSON.stringify(addressData));
        } else if (key === 'workingHours') {
          newFormData.append('workingHours', JSON.stringify(workingHoursData));
        } else if (key !== 'restaurant') {
          newFormData.append(key, value);
        }
      }

      // Restaurant verilerini JSON string olarak ekle
      newFormData.append('restaurant', JSON.stringify(restaurantData));

      const response = await api.post('/api/business/auth/register', newFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data?.success) {
        const { business, restaurantId } = response.data.data;
        
        // Token artık httpOnly cookie'de, localStorage'a kaydetme
        localStorage.setItem('business', JSON.stringify(business));
        localStorage.setItem('restaurantId', restaurantId);
        localStorage.setItem('userType', 'business');
        
        return {
          success: true,
          data: response.data.data
        };
      }
      
      throw new Error(response.data?.message || 'Kayıt başarısız');
      
    } catch (error) {
      console.error('Register error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      const registerError = new Error(error.response?.data?.message || 'Kayıt yapılırken bir hata oluştu');
      registerError.status = error.response?.status;
      registerError.details = error.response?.data?.details;
      throw registerError;
    }
  },

  dismissProfileReminder: async () => {
    await api.post('/api/business/auth/dismiss-profile-reminder', {});
    const raw = localStorage.getItem('business');
    if (!raw) return;
    try {
      const business = JSON.parse(raw);
      localStorage.setItem(
        'business',
        JSON.stringify({ ...business, profileCompletionReminder: false })
      );
    } catch (_) {
      /* ignore */
    }
  },

  logout: async () => {
    // Backend'e logout isteği gönder (cookie'leri temizlemek için)
    try {
      await api.post('/api/business/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // localStorage'ı temizle
    localStorage.removeItem('token');
    localStorage.removeItem('business');
    localStorage.removeItem('restaurantId');
    localStorage.removeItem('userType');
  },

  isAuthenticated: () => {
    // Token artık cookie'de, sadece business bilgilerini kontrol et
    const business = localStorage.getItem('business');
    const restaurantId = localStorage.getItem('restaurantId');
    return !!(business && restaurantId);
  },

  getToken: () => {
    // Token artık httpOnly cookie'de, JavaScript ile erişilemez
    // Backend cookie'den otomatik okuyacak
    return null; // Frontend'de token'a ihtiyaç yok
  },

  getCurrentBusiness: () => {
    const business = localStorage.getItem('business');
    return business ? JSON.parse(business) : null;
  },

  getCurrentRestaurantId: () => {
    return localStorage.getItem('restaurantId');
  },

  staffLogin: async (credentials) => {
    try {
      const response = await api.post('/api/business/auth/staff/login', credentials);

      if (response.data?.success && response.data?.data?.token) {
        const { token, employee, business, restaurantId } = response.data.data;
        
        if (!employee) {
          throw new Error('Çalışan bilgileri alınamadı');
        }

        // Token ve kullanıcı bilgilerini localStorage'a kaydet
        localStorage.setItem('token', token);
        localStorage.setItem('employee', JSON.stringify(employee));
        if (business) {
          localStorage.setItem('business', JSON.stringify(business));
        }
        localStorage.setItem('restaurantId', restaurantId);
        localStorage.setItem('userType', 'employee');
        
        return {
          success: true,
          data: { token, employee, business, restaurantId }
        };
      }
      
      throw new Error(response.data?.message || 'Giriş başarısız');
      
    } catch (error) {
      console.error('Staff login error:', error);
      
      if (error.response?.data?.message) {
        const staffError = new Error(error.response.data.message);
        staffError.status = error.response.data.status || error.response?.status;
        throw staffError;
      }
      
      const staffError = new Error(error.message || 'Giriş yapılırken bir hata oluştu');
      staffError.status = error.response?.status;
      throw staffError;
    }
  },

  getCurrentUser: () => {
    const userType = localStorage.getItem('userType');
    
    if (userType === 'employee') {
      const employee = localStorage.getItem('employee');
      const restaurantId = localStorage.getItem('restaurantId');
      
      if (!employee || !restaurantId) return null;
      
      try {
        const employeeData = JSON.parse(employee);
        return {
          ...employeeData,
          restaurantId,
          userType: 'employee'
        };
      } catch (error) {
        console.error('Employee parse error:', error);
        return null;
      }
    } else {
      const business = localStorage.getItem('business');
      const restaurantId = localStorage.getItem('restaurantId');
      
      if (!business || !restaurantId) return null;
      
      try {
        const businessData = JSON.parse(business);
        return {
          ...businessData,
          restaurantId,
          userType: 'business'
        };
      } catch (error) {
        console.error('Business parse error:', error);
        return null;
      }
    }
  },

  getCurrentRole: () => {
    const user = authService.getCurrentUser();
    return user?.role || null;
  },

  isManager: () => {
    const user = authService.getCurrentUser();
    return user?.role === 'manager' || user?.userType === 'business';
  },

  isStaff: () => {
    const user = authService.getCurrentUser();
    return user?.role === 'staff' && user?.userType === 'employee';
  }
};

export default authService; 